import { AddItemButton, Checkbox, makeStyles, useTranslation } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import DragHandleIcon from '@material-ui/icons/DragHandle'
import { useUser } from '@paintscout/ui-business'
import type { QuoteItemSection, RenderableItem } from '@paintscout/util/builder'
import classNames from 'classnames'
import React, { useEffect, useRef, useState } from 'react'
import type { SortEndHandler, SortStartHandler, SortableContainerProps, SortableElementProps } from 'react-sortable-hoc'
import { arrayMove, SortableContainer, SortableElement } from 'react-sortable-hoc'
import type { Item } from '../ItemTable'
import { resolveAddAnchorKey, resolveAddIndexAfterItemMove, sanitizeAddIndex } from '../ItemTable/add-index'
import OutlinedTableCard, { CHECKBOX_RADIUS, CheckedCheckboxIcon } from './OutlinedTableCard'

export interface OutlinedItemTableProps {
  items: Item[]
  selectedItems?: Item[]
  draggable?: boolean
  checkboxes?: boolean
  showAddButton?: boolean
  addButtonLabel?: React.ReactNode
  addIndex?: number
  addButtonDraggable?: boolean
  section?: QuoteItemSection
  disabled?: boolean

  /** Tier-2 band column label (e.g. "Item"). */
  title?: React.ReactNode
  /** Right-aligned tier-2 band content (e.g. a currency symbol). */
  headerRight?: React.ReactNode
  /** Hide the tier-2 band entirely (section provides its own). */
  hideHeader?: boolean
  /** Tier-1 white section strip title (e.g. "Options"). Enables the two-tier look. */
  sectionTitle?: React.ReactNode
  /** Right-aligned content in the tier-1 section strip (e.g. a Hide pill / signature). */
  sectionAction?: React.ReactNode
  /** Right-aligned helper copy in the tier-1 section strip, left of `sectionAction`. */
  sectionSubtitle?: React.ReactNode
  /** Extra class for the tier-1 section strip (forwarded to OutlinedTableCard). */
  sectionStripClassName?: string
  /** Bump the section title a step (customer-facing Options). */
  largeSectionTitle?: boolean
  /** When defined, the band + rows + footer collapse/expand under the section strip. */
  collapsed?: boolean
  /** Tier-2 band fill variant (defaults to `light`). */
  headerVariant?: 'slate' | 'light' | 'dark'
  /** Collapse the double-border frame to a single bordered card (forwarded to OutlinedTableCard). */
  singleBorder?: boolean
  /**
   * Replace the full-bleed row-to-row border with an inset divider plus breathing room above/below
   * each row (matches `WorkOrderCardNext`'s row rule) instead of a border flush with the table edges.
   */
  insetRowDividers?: boolean

  /** Renders the content of a single item row (reuse ItemColumn + ActionColumn). */
  renderRowColumns: (item: Item, index?: number) => JSX.Element | JSX.Element[]

  onItemClick?: (event: React.MouseEvent<HTMLElement>, item: Item) => void
  onItemSelection?: (event: React.ChangeEvent<HTMLElement>, selectedItems: Item[]) => void
  onReorder?: (args: { items: Item[]; addIndex?: number }) => void
  onAddClick?: (event: React.MouseEvent<HTMLElement>, insertBeforeKey: string, type?: string) => void
  className?: string
}

const useStyles = makeStyles<Theme, OutlinedItemTableProps>(
  (theme) => ({
    // 16px all round with a 16px gap between the checkbox and content, so the checkbox sits at 16px
    // and the item title lines up with the "Item" header title (measured from the mock).
    row: {
      position: 'relative',
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(2),
      padding: theme.spacing(2),
      borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
    },
    // The react-sortable-hoc wrappers sit between the body and each `.row`, so a CSS `:last-child`
    // rule can't reach the final row — drop the trailing divider explicitly instead.
    rowNoBorder: {
      borderBottom: 'none'
    },
    // `insetRowDividers`: a standalone divider between rows instead of each row's own border, so it
    // reads as an inset rule with a little air above/below rather than one flush with the table edges.
    rowDivider: {
      padding: theme.spacing(0.5, 2)
    },
    rowDividerLine: {
      borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
    },
    rowClickable: ({ onItemClick }) => ({
      cursor: onItemClick ? 'pointer' : 'default',
      '&:hover': {
        backgroundColor: onItemClick ? theme.paintscout.palette.brand.Neutral.P98 : undefined
      }
    }),
    checkboxCell: {
      // keep the checkbox aligned with the first line of the row content
      paddingTop: 1,
      '@media print': {
        display: 'none'
      }
    },
    // Decorative drag affordance pinned to the top-center of the row, revealed only on row hover. The
    // whole row stays draggable (react-sortable-hoc wraps it) — this is just a hint, so it's NOT a
    // drag-handle HOC. `pointer-events: none` keeps it from ever intercepting a grab or click: the row
    // beneath it receives the event, so "pick it up anywhere" holds even over the handle.
    dragHandle: {
      position: 'absolute',
      top: theme.spacing(0.5),
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      alignItems: 'center',
      color: theme.paintscout.palette.brand.Neutral.P70,
      pointerEvents: 'none',
      opacity: 0,
      transition: 'opacity 150ms ease',
      '& svg': {
        fontSize: 20
      },
      // reveal when the row is hovered
      '$row:hover &': {
        opacity: 1
      },
      '@media print': {
        display: 'none'
      }
    },
    checkbox: {
      padding: 0,
      // MUI's checked/indeterminate glyph draws its box *inset* in a 24-unit viewBox (spans 3→21, so
      // 18 of 24 units), which means the default 24px font size renders an 18px box — exactly matching
      // the unchecked div below. Shrinking this to 18 makes the checked state visibly smaller.
      '& svg': {
        fontSize: theme.typography.pxToRem(24)
      }
    },
    // Outlined (empty) unchecked box instead of the shared Checkbox's grey fill.
    emptyCheckboxIcon: {
      backgroundColor: 'transparent !important',
      border: `1px solid ${theme.paintscout.palette.brand.Neutral.P80}`,
      borderRadius: CHECKBOX_RADIUS,
      boxSizing: 'border-box'
    },
    // The row content grows to fill the row; ItemColumn already lays out title/description/price.
    content: {
      flex: 1,
      minWidth: 0
    },
    draggingHelper: {
      zIndex: 2_147_483_002,
      background: theme.paintscout.palette.brand.Neutral.White,
      boxShadow: theme.shadows[3],
      // the sortable helper is detached from the card, so give it the row borders back
      border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      borderRadius: theme.borderRadius.md
    },
    // The add button dragged up between items renders as a row inside the item block.
    addRowInline: ({ insetRowDividers }) => ({
      padding: theme.spacing(1),
      borderBottom: insetRowDividers ? undefined : `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
    }),
    addButton: {
      width: '100%',
      justifyContent: 'center',
      border: `1px dashed ${theme.paintscout.palette.brand.Neutral.P85} !important`,
      borderRadius: `${theme.borderRadius.lg} !important`,
      backgroundColor: `${theme.paintscout.palette.brand.Neutral.White} !important`,
      color: theme.palette.primary.main,
      fontWeight: 600,
      minHeight: 52,
      '&:hover': {
        borderColor: `${theme.paintscout.palette.brand.Blue.Blue55} !important`
      }
    }
  }),
  { name: 'OutlinedItemTable' }
)

/** A container-relative position, as react-sortable-hoc stores it. */
interface EdgeOffset {
  left: number
  top: number
}

/**
 * The react-sortable-hoc 2.0 internals `handleSortMeasure` corrects. Neither is in the library's
 * typings, so check them again when the package is upgraded.
 */
interface SortableContainerInternals {
  /** The node every offset is measured against. */
  container?: HTMLElement
  /** Where the picked-up node started. */
  offsetEdge?: EdgeOffset
}

/** What `onSortStart` really passes as `nodes`: the manager's refs, not the bare elements. */
interface SortableManagerRef {
  node: HTMLElement
  edgeOffset?: EdgeOffset
}

const SortableRow: React.ComponentClass<RowProps & SortableElementProps, any> = SortableElement(Row)
const SortableBody: React.ComponentClass<BodyProps & SortableContainerProps, any> = SortableContainer(Body)
const SortableAddButton: React.ComponentClass<AddButtonProps & SortableElementProps, any> = SortableElement(AddButton)

/**
 * The div/card-based analog of `ItemTable` for the new (estimateNext) quote pipeline. Same prop
 * contract as `ItemTable` (minus the table-specific `renderHeadColumns`, replaced by `title` +
 * `headerRight` header slots) so section tables (BidTableNext, …) are near-mechanical ports of
 * their table-based counterparts. Rows are outlined rows inside an `OutlinedTableCard`; drag-reorder
 * reuses `react-sortable-hoc` (element-agnostic) with the add-button index logic ported from
 * `ItemTable`.
 */
export default function OutlinedItemTable(props: OutlinedItemTableProps) {
  const {
    renderRowColumns,
    draggable,
    onReorder,
    onItemSelection,
    selectedItems = [],
    checkboxes = true,
    items,
    showAddButton,
    addButtonLabel,
    addIndex,
    addButtonDraggable = true,
    onAddClick,
    onItemClick,
    section,
    disabled = false,
    title,
    headerRight,
    hideHeader,
    sectionTitle,
    sectionAction,
    sectionSubtitle,
    sectionStripClassName,
    largeSectionTitle,
    collapsed,
    headerVariant,
    singleBorder,
    insetRowDividers,
    className
  } = props
  const classes = useStyles(props)
  const { __ } = useTranslation()

  const { isCrew, isOfficeUser } = useUser()
  const [isDragging, setDragging] = useState(false)
  const dragStart = useRef(0)
  const sortableBodyRef = useRef<React.Component<BodyProps & SortableContainerProps> | null>(null)

  useEffect(() => {
    // easiest way to toggle 'grabbing' cursor - https://github.com/clauderic/react-sortable-hoc/issues/328#issuecomment-524101055
    if (isDragging) {
      document.body.style.cursor = 'grabbing'
    } else {
      document.body.style.cursor = 'default'
    }

    // react-sortable-hoc never cancels the mousedown, so the pointer movement during the drag grows a
    // native text selection that highlights every row the helper passes over (ENG-2000). Block it
    // on the body only while a drag is live, so text in the rows stays selectable otherwise.
    if (!isDragging) {
      return
    }
    document.body.style.userSelect = 'none'
    document.body.style.webkitUserSelect = 'none'
    return () => {
      document.body.style.userSelect = ''
      document.body.style.webkitUserSelect = ''
    }
  }, [isDragging])

  function handleItemSelection(e: any, checked: boolean, item: RenderableItem) {
    e.stopPropagation()
    if (!onItemSelection) {
      return
    }
    onItemSelection(e, checked ? [...selectedItems, item] : selectedItems.filter((i) => i.key !== item.key))
  }

  function handleAllSelection(ev: any, checked: boolean) {
    return onItemSelection && onItemSelection(ev, checked ? items : [])
  }

  // The add button is a sortable row too (ported from `ItemTable`), so sort indices are *row*
  // indices: with the button shown, every item at or after `addRow` sits one row further down.
  const addRow = sanitizeAddIndex(addIndex, items.length)
  const toRowIndex = (itemIndex: number) => (showAddButton && itemIndex >= addRow ? itemIndex + 1 : itemIndex)
  const toItemIndex = (rowIndex: number) => (showAddButton && rowIndex > addRow ? rowIndex - 1 : rowIndex)
  const noReorderForUser = (isOfficeUser || isCrew) && section === 'bid'

  const handleSortStart: SortStartHandler = (_sort, _ev) => {
    dragStart.current = Date.now()
    // the press delay leaves time for a selection to start before the drag does (ENG-2000)
    window.getSelection?.()?.removeAllRanges()
    setDragging(true)
  }

  // react-sortable-hoc 2.0 measures each sortable by adding up `offsetTop` at every DOM level up to
  // the container. That is correct only when each level is the offsetParent of the level below it,
  // and nothing in this card is positioned. A row sits five levels deep in the card and the detached
  // add button one level deep, so the rows measured far below the button. Picking the button up
  // shuffled every row down, and it could only drop at the top (ENG-2021). Overwrite the library's
  // cached offsets with the real ones, measured against the container, before the first move.
  const handleSortMeasure: SortStartHandler = ({ node, nodes }) => {
    const sortableBody = sortableBodyRef.current as unknown as SortableContainerInternals | null
    const container = sortableBody?.container
    // The library sets `offsetEdge` before it calls `onSortStart`. If it is missing, an upgrade changed
    // the internals and this fix no longer applies: say so instead of failing silently.
    if (!sortableBody || !container || !sortableBody.offsetEdge) {
      console.warn('OutlinedItemTable: react-sortable-hoc internals changed; drag offsets are not corrected')
      return
    }
    const origin = container.getBoundingClientRect()
    const edgeOffset = (el: Element): EdgeOffset => {
      const rect = el.getBoundingClientRect()
      return { left: rect.left - origin.left, top: rect.top - origin.top }
    }
    // The typings call these elements; at runtime they are the manager's refs.
    for (const ref of nodes as unknown as SortableManagerRef[]) {
      ref.edgeOffset = edgeOffset(ref.node)
    }
    sortableBody.offsetEdge = edgeOffset(node)
  }

  const handleSortEnd: SortEndHandler = ({ newIndex, oldIndex }, ev) => {
    setDragging(false)
    const dragTime = Date.now() - dragStart.current
    const isAddButton = showAddButton && oldIndex === addRow
    const wasMoved = newIndex !== oldIndex

    // treat it as a regular click if it wasn't moved and was dragged for < 1 second
    if (!wasMoved && dragTime < 1000) {
      if (isAddButton) {
        // same anchor as the AddItemButton's own onClick - insert where the button is drawn
        onAddClick?.(ev as any, resolveAddAnchorKey(items, addIndex), section)
      } else {
        onItemClick?.(ev as any, items[toItemIndex(oldIndex)] as Item)
      }
    }

    if (!onReorder || !wasMoved) {
      return
    }

    if (!showAddButton) {
      onReorder({ items: arrayMove(items, oldIndex, newIndex), addIndex })
    } else if (isAddButton) {
      onReorder({ items, addIndex: newIndex === items.length ? -1 : sanitizeAddIndex(newIndex, items.length) })
    } else {
      // stand the add button in as an `undefined` row, move, then drop it back out
      const rowsWithAddButton = [...items.slice(0, addRow), undefined, ...items.slice(addRow)]
      onReorder({
        items: arrayMove(rowsWithAddButton, oldIndex, newIndex).filter((x) => x !== undefined),
        addIndex: resolveAddIndexAfterItemMove({ addIndex, numItems: items.length, oldIndex, newIndex })
      })
    }
  }

  function cancelDrag(event: any) {
    // composedPath is standard; event.path is non-standard and absent on iOS Safari
    const path: EventTarget[] = event.composedPath ? event.composedPath() : event.path ?? []

    // Walk up from the target to the row boundary to detect clicks inside a control (checkbox,
    // action button) that shouldn't start a drag. Rows carry a `no-drag` marker on control wrappers.
    for (const el of path) {
      if (!(el instanceof Element)) continue
      if (el.getAttribute('data-outlined-row') !== null) break
      const cls = el.getAttribute('class') || ''
      if (cls.includes('no-drag') || cls.includes('SwitchBase') || cls.includes('MuiIconButton')) {
        return true
      }
    }

    // srcElement is non-standard and absent on iOS Safari — use target as fallback
    const target = event.srcElement ?? event.target
    if (!target) return false
    const className = target.className
    if (
      className?.baseVal?.includes('SvgIcon') ||
      (typeof className === 'string' && (className.includes('no-drag') || className.includes('SwitchBase')))
    ) {
      return true
    }

    return false
  }

  // No rows means nothing to select — an empty section's header carries no checkbox (ENG-1478).
  const selectAll =
    checkboxes && onItemSelection && items.length > 0
      ? {
          checked: items.length > 0 && selectedItems.length === items.length,
          indeterminate: selectedItems.length > 0 && selectedItems.length < items.length,
          onChange: handleAllSelection
        }
      : undefined

  // The add button is a fully dashed-bordered box. In its default last slot it sits detached below
  // the item block (a gap between them); dragged up between items (ENG-2021) it renders inline as a
  // row, and new items are inserted where it is drawn.
  const addButtonIsLast = addRow === items.length
  const addButton = showAddButton ? (
    <SortableAddButton
      index={addRow}
      disabled={!draggable || !addButtonDraggable || noReorderForUser || items.length === 0}
    >
      {/* `data-outlined-row` stops cancelDrag's ancestor walk here, the same boundary a row uses */}
      <div className={classNames({ [classes.addRowInline]: !addButtonIsLast })} data-outlined-row>
        <AddItemButton
          className={classes.addButton}
          disabled={disabled}
          onClick={(ev) => onAddClick?.(ev, resolveAddAnchorKey(items, addIndex), section)}
        >
          {addButtonLabel ?? __('Add')}
        </AddItemButton>
      </div>
    </SortableAddButton>
  ) : undefined

  return (
    // The container wraps the whole card, not only the rows, so the detached add button below the
    // item block is still one of its sortable elements.
    <SortableBody
      ref={sortableBodyRef}
      updateBeforeSortStart={handleSortStart}
      onSortStart={handleSortMeasure}
      onSortEnd={handleSortEnd}
      helperClass={`${classes.draggingHelper} draggingHelper`}
      lockAxis={'y'}
      pressDelay={150}
      shouldCancelStart={cancelDrag}
      useWindowAsScrollContainer={true}
    >
      <OutlinedTableCard
        className={className}
        sectionTitle={sectionTitle}
        sectionAction={sectionAction}
        sectionSubtitle={sectionSubtitle}
        sectionStripClassName={sectionStripClassName}
        largeSectionTitle={largeSectionTitle}
        collapsed={collapsed}
        headerVariant={headerVariant}
        singleBorder={singleBorder}
        title={title}
        headerRight={headerRight}
        hideHeader={hideHeader}
        // With no rows, whichever header is last in the inner block sits directly on the card's own bottom
        // border, so its divider reads as a doubled line. The add button is a *detached* footer (rendered
        // outside the inner block), so it never fills that gap. Drop the trailing divider in both shapes:
        // the tier-1 strip when it's the only header, else the tier-2 band.
        hideSectionDivider={hideHeader && items.length === 0}
        hideHeaderDivider={items.length === 0}
        selectAll={selectAll}
        detachedFooter={addButtonIsLast ? addButton : undefined}
      >
        <div>
          {items.map((item, index) => {
            const isLastRow = index === items.length - 1
            const itemIsSelected = !!selectedItems.find((i) => i.key === item.key)
            const rowDisabled = noReorderForUser || !draggable

            return (
              <React.Fragment key={item.key}>
                {!addButtonIsLast && index === addRow && addButton}
                {insetRowDividers && index > 0 && (
                  <div className={classes.rowDivider}>
                    <div className={classes.rowDividerLine} />
                  </div>
                )}
                <SortableRow
                  index={toRowIndex(index)}
                  disabled={rowDisabled}
                  classes={classes}
                  onItemClick={onItemClick ? (ev) => onItemClick(ev, item as Item) : undefined}
                >
                  <div
                    className={classNames(classes.row, classes.rowClickable, {
                      [classes.rowNoBorder]: isLastRow || insetRowDividers
                    })}
                    data-outlined-row
                  >
                    {checkboxes && (
                      <div className={classNames(classes.checkboxCell, 'no-drag')}>
                        <Checkbox
                          className={classes.checkbox}
                          classes={{ uncheckedIcon: classes.emptyCheckboxIcon }}
                          checkedIcon={<CheckedCheckboxIcon />}
                          checked={itemIsSelected}
                          onClick={(e) => handleItemSelection(e, !itemIsSelected, item as RenderableItem)}
                        />
                      </div>
                    )}
                    {!rowDisabled && (
                      <div className={classes.dragHandle} aria-hidden>
                        <DragHandleIcon />
                      </div>
                    )}
                    <div className={classes.content}>{renderRowColumns(item as Item, index)}</div>
                  </div>
                </SortableRow>
              </React.Fragment>
            )
          })}
        </div>
      </OutlinedTableCard>
    </SortableBody>
  )
}

interface AddButtonProps {
  children?: React.ReactNode
}

function AddButton({ children }: AddButtonProps) {
  return <div>{children}</div>
}

interface RowProps {
  children?: React.ReactNode
  onItemClick?: (event: React.MouseEvent<HTMLElement>) => void
  classes: Record<string, string>
}

function Row({ children, onItemClick }: RowProps) {
  return (
    <div onClick={onItemClick} role={onItemClick ? 'button' : undefined}>
      {children}
    </div>
  )
}

interface BodyProps {
  children?: React.ReactNode
}

function Body({ children }: BodyProps) {
  return <div>{children}</div>
}
